import { Link } from '@tanstack/react-router';
import { CaretRight, ChartBar, Key } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectResponse } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { formatDayYear } from '../../lib/project-display';
import { TASK_TYPE_META, weekdayDay } from '../../lib/task-display';
import { waitingQuery } from '../../lib/tasks-api';
import { Icon } from '../Icon';
import { homeCtaClassName } from '../ui/home-cta-button';
import { StatusTag } from '../ui/status-tag';
import { useToast } from '../ui/toast';
import { toasts } from '../../lib/toast-presets';

/** The side cards of the client's Projects pages (Figma C03.1 / C03.2): white, 24 radius, a soft lift. */
export const SIDE_CARD =
  'flex flex-col gap-(--mbm-space-3-5) rounded-(--mbm-component-radius-widget) p-(--mbm-space-5-5) shadow-(--mbm-shadow-table-card)';

/** Figma C03.1 "Waiting for you": the tasks the team has asked this client to review, across every project. */
export function WaitingForYouCard() {
  const { data } = useQuery({ ...waitingQuery(), retry: false });
  const items = data?.items ?? [];
  if (!data || items.length === 0) return null;

  return (
    <section aria-labelledby="waiting-title" className={cx(SIDE_CARD, 'bg-(image:--mbm-gradient-card-wash-blue)')}>
      <h2 id="waiting-title" className="text-heading-h3 text-text-primary">
        Waiting for you
      </h2>
      <ul className="flex flex-col gap-(--mbm-space-3-5)">
        {items.map((task) => (
          <li key={task.id}>
            <Link
              to="/subdomain/app/work/review/$taskId"
              params={{ taskId: task.id }}
              className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-radius-md) bg-surface-card p-(--mbm-space-3) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
                <span className="truncate text-label-large text-text-primary">{task.title}</span>
                <span className="text-body-xsmall text-text-tertiary">
                  {TASK_TYPE_META[task.taskType].short}
                  {task.deadline ? ` · due ${weekdayDay(task.deadline)}` : ''}
                </span>
              </span>
              <StatusTag label="Review" tone="blue" />
            </Link>
          </li>
        ))}
      </ul>
      {/* One goes straight to it; several start with the one due soonest (the list is in that order). */}
      <Link to="/subdomain/app/work/review/$taskId" params={{ taskId: items[0]!.id }} className={homeCtaClassName}>
        <Icon icon={ChartBar} size="md" />
        Review {data.total} {data.total === 1 ? 'task' : 'tasks'}
      </Link>
    </section>
  );
}

const ACCOUNT_ROW =
  'flex w-full items-center gap-(--mbm-space-3) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

function AccountRow({ icon, title, hint }: { icon: typeof Key; title: string; hint: string }) {
  return (
    <>
      <span className="flex size-(--mbm-size-icon-tile) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-tint text-text-primary">
        <Icon icon={icon} size="md" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-label-large text-text-primary">{title}</span>
        <span className="text-body-xsmall text-text-tertiary">{hint}</span>
      </span>
      <span className="text-text-muted">
        <Icon icon={CaretRight} size="sm" />
      </span>
    </>
  );
}

/**
 * Figma C03.1 "Your account": shortcuts to the account's deliverables and this month's allowance.
 * TODO(api): the allowance row needs billing (Sprint 4); it is drawn as designed and says so.
 */
export function YourAccountCard() {
  const toast = useToast();
  return (
    <section aria-labelledby="account-title" className={cx(SIDE_CARD, 'bg-surface-card')}>
      <h2 id="account-title" className="text-heading-h3 text-text-primary">
        Your account
      </h2>
      <ul className="flex flex-col gap-(--mbm-space-3-5)">
        <li>
          <Link to="/subdomain/app/projects/deliverables" className={ACCOUNT_ROW}>
            <AccountRow icon={Key} title="Account deliverables" hint="Logins, brand files, links" />
          </Link>
        </li>
        <li>
          <button
            type="button"
            onClick={() => toast(toasts.comingSoon('This arrives with billing.'))}
            className={ACCOUNT_ROW}
          >
            <AccountRow icon={ChartBar} title="This month’s allowance" hint="Posts, reels and website updates used" />
          </button>
        </li>
      </ul>
    </section>
  );
}

export function NeedSomethingNote() {
  return (
    <section className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-component-radius-widget) bg-surface-tint p-(--mbm-space-5-5)">
      <h2 className="text-label-large text-text-primary">Need something new?</h2>
      <p className="text-body-small text-text-tertiary">Posts, reels and website updates start in Work →</p>
    </section>
  );
}

/** Figma C03.2 headline: the stored rollup, big, over a bar in the project's colour. */
export function ProjectProgressCard({ project }: { project: ProjectResponse }) {
  return (
    <section className={cx(SIDE_CARD, 'bg-(image:--mbm-gradient-card-wash-pink)')}>
      <p className="flex items-end gap-(--mbm-space-2-5) text-text-strong">
        <span className="text-metric-xl">{project.progress}</span>
        <span className="pb-(--mbm-space-1) text-heading-h2 text-accent-marketing">%</span>
        <span className="pb-(--mbm-space-1-5) text-body-compact text-text-secondary">
          {project.taskCount === 0 ? 'No tasks yet' : `${project.doneTaskCount} of ${project.taskCount} tasks done`}
        </span>
      </p>
      <div
        role="progressbar"
        aria-label="Project progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={project.progress}
        className="h-(--mbm-space-2) w-full overflow-hidden rounded-full bg-surface-field"
      >
        <div className="h-full rounded-full bg-accent-marketing" style={{ width: `${project.progress}%` }} />
      </div>
    </section>
  );
}

/**
 * Figma C03.2 "This month's allowance". TODO(api): usage per plan needs billing; the bars are drawn empty with "—"
 * until it exists, and the "rounds of changes" line is left out rather than quote a number the API cannot back.
 */
export function AllowanceCard() {
  return (
    <section className={cx(SIDE_CARD, 'bg-surface-card')}>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">This month’s allowance</h2>
      </div>
      {['Posts', 'Reels'].map((label) => (
        <div key={label} className="flex flex-col gap-(--mbm-space-1-5)">
          <div className="flex items-baseline justify-between text-body-small">
            <span className="text-text-secondary">{label}</span>
            <span className="text-label-medium text-text-primary">—</span>
          </div>
          <div className="h-(--mbm-space-2) w-full rounded-full bg-surface-field" />
        </div>
      ))}
      <p className="text-body-xsmall text-text-tertiary">Usage appears here once billing is live.</p>
    </section>
  );
}

/**
 * Figma C03.2 "Milestones". TODO(api): a project's own milestones (dated checkpoints) have no table yet, so the card
 * is drawn as designed with nothing in it.
 */
export function MilestonesCard({ project }: { project: Pick<ProjectResponse, 'targetEndDate'> }) {
  return (
    <section className={cx(SIDE_CARD, 'bg-surface-card')}>
      <h2 className="text-heading-h3 text-text-primary">Milestones</h2>
      <p className="text-body-small text-text-tertiary">
        {project.targetEndDate
          ? `Your team’s checkpoints for this project appear here. It’s planned to finish ${formatDayYear(project.targetEndDate)}.`
          : 'Your team’s checkpoints for this project appear here.'}
      </p>
    </section>
  );
}
